import { Box, Button, Dialog, DialogContent, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

function OtpVerificationDialog({
  open,
  senderMobile,
  otp,
  onOtpChange,
  onOtpKeyDown,
  inputRefs,
  onVerify,
  onResend,
  canResend,
  loading,
  remainingTime,
}) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      fullScreen={isPhone}
      disableEscapeKeyDown
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(9, 13, 38, 0.68)",
          backdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "calc(100% - 48px)" },
          maxWidth: 460,
          borderRadius: isPhone ? 0 : 5,
          overflow: "hidden",
          background:
            "linear-gradient(180deg, #f7f8ff 0%, #ffffff 42%, #ffffff 100%)",
          border: isPhone ? "none" : "1px solid rgba(22, 27, 77, 0.1)",
          boxShadow: "0 28px 80px rgba(7, 12, 35, 0.38)",
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: isPhone ? "100vh" : "auto" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            background: "linear-gradient(135deg, #161b4d, #34429a)",
            color: "#FFD80A",
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
            boxShadow: "0 12px 26px rgba(22, 27, 77, 0.25)",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h5" fontWeight={800} align="center" sx={{ color: "#161b4d" }}>
          Secure Verification
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Enter the 6-digit OTP sent to{" "}
          <Box component="span" sx={{ color: "#161b4d", fontWeight: 700 }}>
            +91 ••••••{senderMobile?.slice(-4)}
          </Box>
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 0.6, sm: 1 }, mt: 3.5 }}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(element) => (inputRefs.current[index] = element)}
              value={digit}
              onChange={(event) => onOtpChange(event.target.value, index)}
              onKeyDown={(event) => onOtpKeyDown(event, index)}
              autoFocus={index === 0}
              autoComplete={index === 0 ? "one-time-code" : "off"}
              inputProps={{
                inputMode: "numeric",
                maxLength: 1,
                "aria-label": `OTP digit ${index + 1}`,
                style: { textAlign: "center", fontSize: "1.35rem", fontWeight: 700, padding: "13px 0" },
              }}
              sx={{
                width: { xs: 40, sm: 48 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  backgroundColor: digit ? "#fffbea" : "#fff",
                  transition: "all 160ms ease",
                  "& fieldset": {
                    borderColor: digit ? "#FFD80A" : "#d9ddea",
                    borderWidth: digit ? 2 : 1,
                  },
                  "&:hover fieldset": { borderColor: "#9299ad" },
                  "&.Mui-focused": {
                    boxShadow: "0 0 0 4px rgba(255, 216, 10, 0.16)",
                  },
                  "&.Mui-focused fieldset": { borderColor: "#d8b600" },
                },
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            mt: 2,
            mx: "auto",
            px: 1.5,
            py: 0.65,
            width: "fit-content",
            borderRadius: 5,
            color: "#16834d",
            backgroundColor: "#eaf8f0",
            fontSize: "0.8rem",
            fontWeight: 700,
          }}
        >
          ● &nbsp; Code valid for {remainingTime || "15 minutes"}
        </Box>
        <Typography variant="caption" align="center" color="text.secondary" sx={{ mt: 1 }}>
          Keep this window open until your transfer is complete.
        </Typography>

        <Button
          fullWidth
          variant="contained"
          size="large"
          type="button"
          onClick={onVerify}
          disabled={loading || otp.some((digit) => !digit)}
          sx={{
            mt: 3,
            py: 1.4,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 800,
            color: "#fff",
            background: "linear-gradient(135deg, #161b4d, #34429a)",
            boxShadow: "0 10px 22px rgba(22, 27, 77, 0.24)",
            "&:hover": {
              background: "linear-gradient(135deg, #10143b, #293577)",
            },
            "&.Mui-disabled": {
              color: "rgba(255, 255, 255, 0.78)",
              background: "#747da9",
            },
          }}
        >
          {loading ? "Verifying..." : "Verify & Transfer"}
        </Button>
        {canResend && (
          <Button
            onClick={onResend}
            disabled={loading}
            sx={{ mt: 1.25, textTransform: "none", fontWeight: 700 }}
          >
            Resend OTP
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default OtpVerificationDialog;
